import deepmerge from "deepmerge";
import { cloneDeep, set } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";

import { getValue, setStyles } from "@intelligems/ig-plugin/core";
import {
  AnyProgressBarWidget,
  AnyProgressBarWidgetDeviceConfig,
  AnyProgressBarWidgetLikeObject,
  AnyProgressBarWidgetTextReplacements,
  AnyProgressBarWidgetVariables,
  AnyProgressBarWidgetWindowState,
  DiscountProgressBarProgress,
  DiscountUnitType,
  DottedJoin,
  ExperienceCurrencyDto,
  MemCartItem,
  OfferDiscountLikeObject,
  OfferDto,
  TiersUnlockedReplacements,
  WidgetDto,
} from "@intelligems/ig-types";
import {
  AnyProgressBarWidgetBase,
  buildRewardSnippet,
  calculateMessages,
  calculateProgressBarPercentage,
  getTooltipMessage,
  getWhatsUnlocked,
  setAnyProgressBarVariables,
  standardTiersUnlockedReplacements,
} from "@intelligems/ig-widgets";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { DiscountStub, TestShippingDiscountStateStub } from "../stubs";

export interface IgDiscountProgressBarProps {
  configType: "mobile" | "desktop";
  progressBarWidget: AnyProgressBarWidget;
  threshold?: number;
  discountState?: OfferDto;
  desktopVariables?: AnyProgressBarWidgetVariables;
}

export const IgShippingProgressBar = ({
  configType,
  progressBarWidget,
  threshold,
  discountState,
  desktopVariables,
}: IgDiscountProgressBarProps) => {
  const currencyFormat: ExperienceCurrencyDto =
    useConfiguration("currency_format")?.value;

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const DiscountState = discountState || TestShippingDiscountStateStub;

  const formatter = new Intl.NumberFormat(
    storeCurrencyConfig?.value?.locale ?? "en",
    {
      style: "currency",
      currency:
        currencyFormat?.code || storeCurrencyConfig?.value?.code || "USD",
      maximumFractionDigits: 0,
    }
  );

  const priceCurrencyFormatter = useCallback(
    (price: number | string, hideCurrency?: boolean): string => {
      if (typeof price === "string") {
        return price;
      }
      return formatter.format(price);
    },
    []
  );

  const currencyFormatter = (
    input: number,
    unitType: DiscountUnitType
  ): string => {
    if (unitType === "dollar") {
      return formatter.format(input);
    }
    return input.toString();
  };

  const ProgressBarWidget = useMemo(() => {
    const resp = cloneDeep(progressBarWidget);
    if (!resp.config[configType]?.variables?.textReplacements) {
      set(resp, `config.${configType}.variables.textReplacements`, {});
    }

    resp.config[configType]!.variables!.textReplacements.buyMoreTemplate =
      resp.config[configType]?.variables?.textReplacements?.buyMoreTemplate ??
      "Almost there! Add #quantity to unlock Free Shipping!";

    if (desktopVariables) {
      resp.config[configType]!.variables!.textReplacements = deepmerge(
        resp.config[configType]!.variables!.textReplacements,
        desktopVariables.textReplacements
      );
    }

    resp.config[configType]!.variables = setAnyProgressBarVariables(
      resp.config[configType]!.variables!
    );

    return resp;
  }, [progressBarWidget, desktopVariables?.textReplacements]);

  const DiscountProgressBarWidgetStub =
    useCallback((): AnyProgressBarWidgetLikeObject => {
      const state: WidgetDto = {
        enabledSitewide: false,
        id: v4(),
        name: "Shipping Progress Bar",
        widgetType: "shippingProgressBar",
        config: {
          desktop: {
            variables: { textReplacements: {} },
          },
        },
      };

      return {
        ...state,
        config: state.config || {},
        buildRewardSnippet: buildRewardSnippet,
        async calculateMessages(
          discount: OfferDiscountLikeObject,
          cartItems: MemCartItem[]
        ) {
          return calculateMessages(
            discount,
            cartItems,
            true,
            priceCurrencyFormatter
          );
        },
        async calculateProgressBarPercentage(
          discount: OfferDiscountLikeObject,
          cartItems: MemCartItem[]
        ) {
          return calculateProgressBarPercentage(discount, cartItems, true);
        },
        async getTooltipMessage(
          discount: OfferDiscountLikeObject | null,
          tierId?: string,
          textReplacements?: AnyProgressBarWidgetTextReplacements
        ) {
          return getTooltipMessage(
            discount,
            tierId,
            ProgressBarWidget.config[configType]?.variables?.textReplacements
          );
        },
        getWhatsUnlocked: getWhatsUnlocked,
        setStyles(): void {},
        async standardTiersUnlockedReplacements(
          text: string,
          options: TiersUnlockedReplacements
        ) {
          return standardTiersUnlockedReplacements(
            text,
            options,
            priceCurrencyFormatter
          );
        },
        toModel(): WidgetDto {
          return state;
        },
      };
    }, [ProgressBarWidget]);

  const [windowState, setWindowState] =
    useState<AnyProgressBarWidgetWindowState>(() => {
      return {
        discount: DiscountStub(DiscountState),
        widget: DiscountProgressBarWidgetStub(),
        cartItems: [],
        progress: {
          section: 0,
          progress: 0,
        },
        unlockedMessage: undefined,
        message: undefined,
        tooltipMessage: "",
      };
    });

  const isMobile = "mobile" === configType;
  const prefix = `ig-pb-${progressBarWidget.id}`;

  const handleMessage = useCallback((e: MessageEvent) => {
    return undefined;
  }, []);

  const getWidgetConfig = useCallback(
    (path: DottedJoin<AnyProgressBarWidgetDeviceConfig>) => {
      let value;
      if (isMobile) {
        value = getValue(ProgressBarWidget.config.mobile, path as string);
      }

      if (!value) {
        value = getValue(ProgressBarWidget.config.desktop, path as string);
      }
      return value;
    },
    [ProgressBarWidget?.config, isMobile]
  );

  useEffect(() => {
    setStyles(
      prefix,
      ProgressBarWidget?.config?.desktop?.styles,
      ProgressBarWidget?.config?.mobile?.styles
    );
  }, [
    ProgressBarWidget?.config?.desktop?.styles,
    ProgressBarWidget?.config?.mobile?.styles,
    prefix,
  ]);

  useEffect(() => {
    if (
      DiscountState &&
      threshold !== undefined &&
      ProgressBarWidget.config[configType] &&
      ProgressBarWidget.config[configType]?.variables
    ) {
      const fetchNewMessage = async () => {
        const cartItems = [
          {
            isVdProduct: true,
            line: "0",
            productId: "a",
            variantId: "b",
            quantity: DiscountState?.discountType === "dollar" ? 1 : threshold,
            price: DiscountState?.discountType === "dollar" ? threshold : 1,
            final_price:
              DiscountState?.discountType === "dollar" ? threshold : 1,
            calculatedUnitDollarDiscount: 0,
            cartMessage: "Cart Message",
          },
        ] as MemCartItem[];
        let progressBarPercentage: DiscountProgressBarProgress = {
          section: 0,
          progress: 0,
        };
        let messages: { message: string; unlockedMessage: string } = {
          message: "",
          unlockedMessage: "",
        };
        await calculateProgressBarPercentage(
          DiscountState as unknown as OfferDiscountLikeObject,
          cartItems,
          true
        ).then((percentageResp) => (progressBarPercentage = percentageResp));
        await calculateMessages(
          DiscountState as unknown as OfferDiscountLikeObject,
          cartItems,
          true,
          priceCurrencyFormatter,
          ProgressBarWidget.config[configType]?.variables?.textReplacements ||
            ProgressBarWidget.config.desktop!.variables!.textReplacements
        ).then((messageResp) => {
          messages = messageResp;
        });
        setWindowState((draft) => {
          const updates = {
            discount: DiscountStub(DiscountState),
            message: messages.message,
            unlockedMessage: messages.unlockedMessage,
            cartItems,
            progress: progressBarPercentage,
          };

          if (!discountState && !threshold) {
            updates["progress"] = {
              section: 0,
              progress: 70,
            };
          }

          return {
            ...draft,
            ...updates,
          };
        });
      };

      void fetchNewMessage();
    }
  }, [threshold, DiscountState, ProgressBarWidget]);

  return (
    <AnyProgressBarWidgetBase
      type={"shipping"}
      prefix={prefix}
      windowState={windowState}
      isMobile={isMobile}
      config={ProgressBarWidget.config}
      getWidgetConfig={getWidgetConfig}
      currencyFormatter={currencyFormatter}
      handleMessage={handleMessage}
    />
  );
};
